import { useRef, useState } from 'react';
import EfImg from '@elem/ui/ef-img';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import { BaseCardBtnText } from 'lowcode/common/LangText/BaseCardBtnText';
import EfLink from '@elem/ui/ef-link';
import getLocal from '@/app/utils/getLocal';
import EfBundlesCalculator from '@elem/ui/ef-bundles-calculator';
import editorEventsBus from 'lowcode/utils/editorEventsBus';
import { formatOffText } from '@/app/eflayout/Header/DropBox/DropBoxSearch';
import gtm from '@/utils/gtm';
import cookie from 'react-cookies';

import './index.scss';

// 定义产品类型
type ProductItem = {
    productId: string;
    variantId: string;
    quantity: number;
    price: string;
    compareAtPrice: string;
    img: string;
    variantImg: string;
    productTitle: string;
    skuTitle: string;
    variantDescription: string;
    handle: string;
    specific: string;
};

// 定义Bundle类型
type BundleItem = {
    itemId: string;
    itemType: string;
    retrieveId: string;
    score: number;
    variants: ProductItem[];
};

export default function BundleProductItem({ bundle, maxItemsForHeight }: any) {
    const { locale = 'us' } = getLocal();
    const prevRef = useRef<HTMLDivElement>(null);
    const nextRef = useRef<HTMLDivElement>(null);
    /**
     * 格式化 variants 数据，使其适用于 EfBundlesCalculator 组件
     */
    const formatVariantsData = (variants: any[]) => {
        return variants.map((variant: any) => ({
            ...variant,
            name: variant?.productTitle || '',
            discountPrice: variant?.price || 0,
            maxCanBuyCount: variant?.inventoryQuantity || 999,
            inventoryQuantity: variant?.inventoryQuantity || 999,
        }));
    };

    const [variantsData, setVariantsData] = useState(formatVariantsData(bundle.variants || [])); // 用于保存选择的子产品

    const totalPrice = variantsData.reduce((sum: number, v: any) => sum + Number(v.price) * v.quantity, 0);
    const totalCompare = variantsData.reduce((sum: number, v: any) => sum + Number(v?.compareAtPrice || v?.price) * v.quantity, 0);
    const [totalBundle, setTotalBundle] = useState({
        discount: totalPrice.toFixed(2),
        original: totalCompare.toFixed(2),
    }); //卡片总价

    const prefixCls = 'temp-member-product-ai-recommend';

    const TextA = BaseCardBtnText[locale] || BaseCardBtnText['us'];

    const getInitBuyHref = (data: any) => {
        let valStr = ''; // 副产品
        for (let i = 0; i < data.length; i++) {
            const itemB = data[i];
            const variantId = itemB.variantId;
            const quantity = itemB.quantity;
            if (variantId && quantity) {
                const linkFlag = i === data.length - 1 ? '' : ',';
                valStr += `${variantId}:${quantity}${linkFlag}`;
            }
        }
        const hrefAll = `https://${locale}.ecoflow.com/cart/${valStr}?channel=buy_button`;
        return hrefAll;
    };

    const initHrefString = getInitBuyHref(bundle.variants);
    const [initHref, setInitHref] = useState(initHrefString); //购买链接

    // 设置购买链接
    const setBuyHrefFn = (subsB2: any) => {
        let valStr = ''; // 副产品
        for (let i = 0; i < subsB2.length; i++) {
            const itemB = subsB2[i];
            const variantId = itemB.variantId;
            const quantity = itemB.quantity;
            if (variantId && quantity) {
                const linkFlag = i === variantsData.length - 1 ? '' : ',';

                valStr += `${variantId}:${quantity}${linkFlag}`;
            }
        }
        // const href1 = `${item.variantId}:1`; //主产品
        const hrefAll = `https://${locale}.ecoflow.com/cart/${valStr}?channel=buy_button`;
        setInitHref(hrefAll);
    };

    // 计算折扣百分比
    const getDiscountPercentage = (price: number, compareAtPrice: number) => {
        if (!compareAtPrice || compareAtPrice <= price) return null;
        const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
        const finalOffText = formatOffText(discount, locale);

        return discount > 0 ? finalOffText : null;
    };

    // 点击购买--打开购物车
    const buyInCartHandle = () => {
        // 收集所有有数量的产品
        const productsToAdd = variantsData
            .filter((variant) => variant.quantity && variant.quantity > 0)
            .map((variant) => ({
                item: {
                    // 必需字段
                    variantId: variant.variantId,
                    productId: variant.productId,

                    // 产品信息
                    name: variant?.productTitle || variant?.skuTitle || '',
                    title: variant?.productTitle || variant?.skuTitle || '',
                    img: variant.variantImg || variant.img,

                    // 价格信息
                    price: variant.price,
                    discountPrice: variant.price,
                    originalPrice: variant.compareAtPrice,
                    compareAtPrice: variant.compareAtPrice,

                    // 库存和限制
                    inventoryQuantity: variant.inventoryQuantity,
                    maxCanBuyCount: variant.maxCanBuyCount || variant.inventoryQuantity || 999,

                    // 其他字段
                    parentVariantId: variant.parentVariantId,
                },
                quantity: variant.quantity,
            }));

        if (productsToAdd.length > 0) {
            const expires = new Date();
            expires.setDate(expires.getDate() + 7);

            const cookieOptions = {
                path: '/',
                domain: '.ecoflow.com',
                expires,
            };

            cookie.save('ai_entry_type', 'AI Brand Member Recommend_Official Cart Popup', { path: '/', domain: '.ecoflow.com' });
            cookie.save('ai_jump_url', 'Official Cart Popup', { path: '/', domain: '.ecoflow.com' });
            cookie.save('display_type', 'AI Brand Member Recommend_Recommend Bundles_Official Cart Popup', cookieOptions);

            try {
                // TODO: 埋点 - 批量添加到购物车
                const totalQuantity = productsToAdd.reduce((sum, product) => sum + (product.quantity || 0), 0);
                const totalPrice = productsToAdd.reduce((sum, product) => sum + product.item.price * (product.quantity || 0), 0);
                const productNames = productsToAdd.map((product) => product.item.name).join(', ');

                gtm.push({
                    event: 'uaEvent',
                    event_name: 'bundle_add_to_cart',
                    list_name: 'You May Also Like',
                    product_name: productNames,
                    price: totalPrice,
                    quantity: totalQuantity,
                    button_name: TextA.AddToCart || 'Add to Cart',
                    tag: 'bundle_batch_add',

                    display_type: 'Recommend Bundles',
                    module_referral: 'AI Brand Member Recommend',
                    jump_module: 'Official Cart Popup',
                });

                // 添加到购物车
                editorEventsBus.emit('efshopingcart-add', productsToAdd, null, 'AI Brand Member Recommend');
            } catch (error) {
                console.error('添加到购物车时发生错误:', error);
            }
        } else {
            console.log('没有选择任何产品或数量为0');
        }
    };

    const handleBuyClick = () => {
        const productsToAdd = variantsData
            .filter((variant) => variant.quantity && variant.quantity > 0)
            .map((variant) => ({
                item: {
                    // 必需字段
                    variantId: variant.variantId,
                    productId: variant.productId,

                    // 产品信息
                    name: variant?.productTitle || variant?.skuTitle || '',
                    title: variant?.productTitle || variant?.skuTitle || '',
                    img: variant.variantImg || variant.img,

                    // 价格信息
                    price: variant.price,
                    discountPrice: variant.price,
                    originalPrice: variant.compareAtPrice,
                    compareAtPrice: variant.compareAtPrice,

                    // 库存和限制
                    inventoryQuantity: variant.inventoryQuantity,
                    maxCanBuyCount: variant.maxCanBuyCount || variant.inventoryQuantity || 999,

                    // 其他字段
                    parentVariantId: variant.parentVariantId,
                },
                quantity: variant.quantity,
            }));

        const expires = new Date();
        expires.setDate(expires.getDate() + 7);

        const cookieOptions = {
            path: '/',
            domain: '.ecoflow.com',
            expires,
        };

        cookie.save('ai_entry_type', 'AI Brand Member Recommend_Recommend Bundles', { path: '/', domain: '.ecoflow.com' });
        cookie.save('ai_jump_url', initHref, { path: '/', domain: '.ecoflow.com' });
        cookie.save('display_type', 'AI Brand Member Recommend_Recommend Bundles_Checkout Page', cookieOptions);

        const productNames = productsToAdd.map((product) => product.item.name).join('_');

        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: `${TextA.BuyNow}_${productNames}` || 'Buy Now',
            display_type: 'Recommend Bundles',
            module_referral: 'AI Brand Member Recommend',
            jump_module: initHref || '',
        });
    };

    // 获取子项的总价
    const getSubTotalFn = (subs2: any) => {
        let discount = 0;
        let original = 0;
        for (let i = 0; i < subs2.length; i++) {
            const item2 = subs2[i];
            const quantity = item2.quantity || 0;

            discount += quantity * (item2?.price || 0);
            original += quantity * (item2?.compareAtPrice || item2?.price || 0);
        }

        return {
            discount,
            original,
        };
    };

    // 数量发生变化
    const numChange = (index2: number, quantity: number) => {
        const variants2 = JSON.parse(JSON.stringify(variantsData));
        variants2[index2].quantity = quantity || 0;
        // 确保数据格式保持一致性，重新应用格式化
        const formattedVariants = formatVariantsData(variants2);
        const totalObj = getSubTotalFn(formattedVariants);
        setBuyHrefFn(formattedVariants);
        setTotalBundle({
            discount: totalObj.discount.toFixed(2),
            original: totalObj.original.toFixed(2),
        });
        setVariantsData(formattedVariants);
    };

    const bundleDiscountLabel = getDiscountPercentage(parseFloat(totalBundle.discount), parseFloat(totalBundle.original));

    // 根据同组内最多产品数量生成CSS类名，确保所有bundle高度一致
    const bundleCardClassName = `${prefixCls}-bundle-card bundle-items-${maxItemsForHeight || 1}`;

    return (
        <div className={bundleCardClassName}>
            {bundleDiscountLabel && <div className={`${prefixCls}-bundle-discount-badge`}>{bundleDiscountLabel}</div>}

            {/* 产品列表 */}
            <div className={`${prefixCls}-bundle-products`}>
                {variantsData?.slice(0, 3).map((variant: any, index2: number) => {
                    return (
                        <div key={variant?.variantId} className={`${prefixCls}-bundle-product-item`}>
                            <div className={`${prefixCls}-bundle-product-image`}>
                                <EfImg pc={variant?.variantImg || variant?.img} />
                            </div>

                            <div className={`${prefixCls}-bundle-product-info`}>
                                <h5 className={`${prefixCls}-bundle-product-title`}>{variant?.productTitle || variant?.skuTitle}</h5>
                                <div className={`${prefixCls}-bundle-product-pricing-box`}>
                                    <div className={`${prefixCls}-bundle-product-pricing`}>
                                        <span className={`${prefixCls}-bundle-product-price`}>{FormatMoneyLocal(parseFloat(variant?.price), locale)}</span>
                                        {/* {variant?.compareAtPrice && ( */}
                                        {variant?.compareAtPrice && variant?.compareAtPrice !== variant?.price && (
                                            <span className={`${prefixCls}-bundle-product-compare-price`}>
                                                {FormatMoneyLocal(parseFloat(variant?.compareAtPrice), locale)}
                                            </span>
                                        )}
                                    </div>

                                    <EfBundlesCalculator
                                        defaultQuantity={variant?.quantity || 0}
                                        item={variant}
                                        callback={(quantity: number) => {
                                            numChange(index2, quantity);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bundle总价和按钮 */}
            <div className={`${prefixCls}-bundle-footer`}>
                <div className={`${prefixCls}-bundle-total-pricing`}>
                    <div className={`${prefixCls}-bundle-total-price-row`}>
                        <span className={`${prefixCls}-bundle-total-current-price`}>{FormatMoneyLocal(parseFloat(totalBundle?.discount), locale)}</span>
                        <div className={`${prefixCls}-bundle-member-tip-box`}>
                            <div className={`${prefixCls}-bundle-member-tip-text`}>{TextA.MembersOnly}</div>
                        </div>
                    </div>

                    {totalBundle.original !== totalBundle.discount && (
                        <span className={`${prefixCls}-bundle-total-original-price`}>
                            {FormatMoneyLocal(parseFloat(totalBundle.original), locale)}
                            {TextA.NonMemberPrice}
                        </span>
                    )}
                </div>

                <div className={`${prefixCls}-bundle-actions`}>
                    <button onClick={buyInCartHandle} className={`${prefixCls}-bundle-btn ${prefixCls}-bundle-btn-cart`}>
                        + {TextA.AddToCart || 'Add to Cart'}
                    </button>

                    <EfLink href={initHref} onClick={handleBuyClick} className={`${prefixCls}-bundle-btn ${prefixCls}-bundle-btn-checkout`}>
                        {TextA.BuyNow || 'Buy Now'}
                    </EfLink>
                </div>
            </div>
        </div>
    );
}
